import { invalidate } from '@react-three/fiber';
import { memo, useCallback, useEffect, useMemo } from 'react';
import {
    type BufferGeometry,
    Color,
    type Object3D,
    Mesh,
    type Material,
    type Group,
    MeshPhysicalMaterial
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { useShallow } from 'zustand/shallow';

import { type PrefabModel } from 'api/models';
import { useSheetMaterialLoader } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useSheetMaterialLoader';
import { ArticleEdgeGeometries } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleEdgeGeometries';
import {
    getMeshMaterial,
    isRaycastEnabled,
    normalizeMeshName
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    applyMaterialQuality,
    getMeshLodLevel
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';
import { useLoadClonedGLTF } from 'app/modules/wallforming/components/scenes/objects/helpers/hooks/useLoadClonedGLTF';
import { GltfAsset } from 'app/modules/wallforming/components/scenes/objects/helpers/types/gltfAsset';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { ArticleMesh } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleMesh';

const IS_DEV_TOOLS = import.meta.env.VITE_DEV_TOOLS === 'true';

interface GLTFLoaderProps {
    articleId: string;
    isMainCanvas: boolean;
    articleRepresentation: PrefabModel;
    gltfAssets: GltfAsset[];
    children: (props: {
        articleId: string;
        isMainCanvas: boolean;
        articleRepresentation: PrefabModel;
        articleMeshes: ArticleMesh[];
    }) => React.ReactNode;
}

export const GLTFLoader = memo(
    ({ articleId, isMainCanvas, articleRepresentation, gltfAssets, children }: GLTFLoaderProps) => {
        // MIND: this component renders multiple times
        // GLTFs can resolve before catalogue metadata registers the article.
        const isArticleRegistered = usePlacementStore((state) => Boolean(state.articles[articleId]));

        const { syncArticleEdgeGeometries, syncArticleInstanceParts } = usePlacementStore(
            useShallow((state) => ({
                syncArticleEdgeGeometries: state.syncArticleEdgeGeometries,
                syncArticleInstanceParts: state.syncArticleInstanceParts
            }))
        );

        const gltfs = useLoadClonedGLTF({
            gltfUrls: gltfAssets.map((asset) => asset.gltfUrl)
        });

        const sheetMaterial = useSheetMaterialLoader();

        const materialQuality = usePlacementStore((state) =>
            isMainCanvas ? state.graphicsSettings.materialQuality : ('basic' as const)
        );

        useEffect(() => {
            const partKeys = [...new Set(gltfAssets.map((asset) => asset.partName).filter((name) => name))];
            if (isArticleRegistered && partKeys.length > 0) {
                syncArticleInstanceParts(articleId, partKeys);
            }
        }, [articleId, gltfAssets, isArticleRegistered, syncArticleInstanceParts]);

        const articleMeshes = useMemo<ArticleMesh[] | null>(() => {
            if (!gltfs || gltfs.length === 0 || gltfs.length !== gltfAssets.length) {
                return null;
            }

            const meshes: ArticleMesh[] = [];
            const selectionGeometries: BufferGeometry[] = [];

            gltfs.forEach((gltf, i) => {
                let partMeshIndex = 0;
                gltf.traverse((child: Object3D) => {
                    if (child instanceof Mesh) {
                        const normalizedChildName = normalizeMeshName(child.name);
                        let material: Material | undefined;

                        if (normalizedChildName !== 'ACWT_0-SHE-DOKA') {
                            material = getMeshMaterial(child.name, child.material.color);
                        } else if (sheetMaterial) {
                            material = sheetMaterial;
                        }

                        if (material) {
                            let defaultColor = new Color(0xffffff);
                            if ('color' in material && material.color instanceof Color) {
                                defaultColor = material.color.clone();
                            }

                            if (normalizedChildName.toLowerCase() !== 'holes') {
                                child.geometry.computeBoundingBox();
                                selectionGeometries.push(child.geometry);
                            }

                            const partName = gltfAssets[i].partName;
                            const meshId = `${partName}::${child.name}::${partMeshIndex}`;
                            partMeshIndex += 1;

                            meshes.push({
                                id: meshId,
                                geometry: child.geometry,
                                material,
                                name: child.name,
                                partName,
                                defaultColor,
                                raycast: isRaycastEnabled(child.name)
                            });
                        }
                    }
                });
            });

            return meshes;
        }, [gltfs, gltfAssets, sheetMaterial]);

        // Mutate material properties in-place when quality changes — avoids remounting instancedMesh
        // and the geometry-disposal side-effect that comes with recreating articleMeshes.
        useEffect(() => {
            if (!articleMeshes || !isMainCanvas) {
                return;
            }
            articleMeshes.forEach((mesh) => {
                const normalizedMeshName = normalizeMeshName(mesh.name);
                if (mesh.material instanceof MeshPhysicalMaterial && normalizedMeshName.toLowerCase() !== 'holes') {
                    applyMaterialQuality(mesh.material, materialQuality, normalizedMeshName === 'ACWT_0-SHE-DOKA');
                }
            });
            invalidate();
        }, [materialQuality, articleMeshes, isMainCanvas]);

        const handleGroupRef = useCallback(
            (group: Group | null) => {
                if (!group || !articleMeshes || !isArticleRegistered) {
                    return;
                }

                const articleEdgeGeometries = usePlacementStore.getState().articles[articleId]?.edgeGeometries;
                const edgeGeometries: Record<string, BufferGeometry[][]> = {};
                articleMeshes
                    .filter((mesh) => normalizeMeshName(mesh.name).toLowerCase() !== 'holes')
                    .forEach((mesh) => {
                        if (!mesh.partName || articleEdgeGeometries?.[mesh.partName]) {
                            return;
                        }

                        const key = mesh.partName;
                        if (!edgeGeometries[key]) {
                            edgeGeometries[key] = [[], [], []];
                        }

                        const lodLevel = getMeshLodLevel(mesh.name);
                        edgeGeometries[key][lodLevel].push(mesh.geometry);
                    });

                const partGeometries: ArticleEdgeGeometries = {};
                for (const [key, lodGeometries] of Object.entries(edgeGeometries)) {
                    partGeometries[key] = lodGeometries.map((geometries) => {
                        if (geometries.length === 0) {
                            return null;
                        }

                        if (geometries.length === 1) {
                            return geometries[0];
                        }

                        return mergeGeometries(geometries) ?? null;
                    });
                }

                if (Object.keys(partGeometries).length > 0) {
                    const meshInfos = IS_DEV_TOOLS
                        ? articleMeshes
                              .filter((m) => m.name !== 'Selection')
                              .map((m) => ({
                                  id: m.id,
                                  name: m.name,
                                  partName: m.partName,
                                  triangles: Math.floor(
                                      m.geometry.index
                                          ? m.geometry.index.count / 3
                                          : m.geometry.attributes.position.count / 3
                                  )
                              }))
                        : [];

                    syncArticleEdgeGeometries(
                        articleId,
                        { ...articleEdgeGeometries, ...partGeometries },
                        articleMeshes.length,
                        meshInfos
                    );
                }
            },
            [articleId, articleMeshes, isArticleRegistered, syncArticleEdgeGeometries]
        );

        if (!gltfs || !articleMeshes || articleMeshes.length === 0) {
            return null;
        }

        return (
            <group ref={handleGroupRef}>
                {children?.({
                    articleId,
                    articleMeshes,
                    articleRepresentation,
                    isMainCanvas
                })}
            </group>
        );
    }
);

GLTFLoader.displayName = 'GLTFLoader';
